import {
  MAP_W,
  MAP_H,
  SAVED_CAL,
  stitchData,
  provinceDims,
  provinceGeo,
} from "./mapConfig"

function gaussElim(A, b) {
  const n = A.length
  const M = A.map((row, i) => row.slice().concat([b[i]]))
  for (let col = 0; col < n; col++) {
    let mx = col
    for (let r = col + 1; r < n; r++) {
      if (Math.abs(M[r][col]) > Math.abs(M[mx][col])) mx = r
    }
    const tmp = M[col]
    M[col] = M[mx]
    M[mx] = tmp
    if (Math.abs(M[col][col]) < 1e-12) return null
    for (let r = col + 1; r < n; r++) {
      const f = M[r][col] / M[col][col]
      for (let j = col; j <= n; j++) M[r][j] -= f * M[col][j]
    }
  }
  const x = new Array(n)
  for (let i = n - 1; i >= 0; i--) {
    x[i] = M[i][n]
    for (let j = i + 1; j < n; j++) x[i] -= M[i][j] * x[j]
    x[i] /= M[i][i]
  }
  return x
}

export function computeHomography(pairs) {
  const n = pairs.length
  if (n < 4) return null
  const A = []
  const b = []
  for (let i = 0; i < n; i++) {
    const p = pairs[i]
    A.push([p.sx, p.sy, 1, 0, 0, 0, -p.dx * p.sx, -p.dx * p.sy])
    b.push(p.dx)
    A.push([0, 0, 0, p.sx, p.sy, 1, -p.dy * p.sx, -p.dy * p.sy])
    b.push(p.dy)
  }
  const m = A.length
  const nn = 8
  const AtA = []
  const Atb = []
  for (let i = 0; i < nn; i++) {
    AtA[i] = []
    for (let j = 0; j < nn; j++) {
      let s = 0
      for (let k = 0; k < m; k++) s += A[k][i] * A[k][j]
      AtA[i][j] = s
    }
    let s2 = 0
    for (let k = 0; k < m; k++) s2 += A[k][i] * b[k]
    Atb[i] = s2
  }
  const x = gaussElim(AtA, Atb)
  if (!x) return null
  return [[x[0], x[1], x[2]], [x[3], x[4], x[5]], [x[6], x[7], 1]]
}

export function transformPoint(H, lng, lat) {
  const d = H[2][0] * lng + H[2][1] * lat + H[2][2]
  return {
    px: (H[0][0] * lng + H[0][1] * lat + H[0][2]) / d,
    py: (H[1][0] * lng + H[1][1] * lat + H[1][2]) / d,
  }
}

export function buildCalLookup(calPoints) {
  const calByProvince = {}
  const ids = Object.keys(provinceGeo)
  for (let i = 0; i < ids.length; i++) {
    const id = ids[i]
    const geo = provinceGeo[id]
    if (!geo || !geo.capital) continue
    for (let j = 0; j < calPoints.length; j++) {
      const cp = calPoints[j]
      if (
        Math.abs(cp.lng - geo.capital.lng) < 0.01 &&
        Math.abs(cp.lat - geo.capital.lat) < 0.01
      ) {
        calByProvince[id] = cp
        break
      }
    }
  }
  return calByProvince
}

export function createHomography() {
  const calPoints = JSON.parse(JSON.stringify(SAVED_CAL))
  const pairs = calPoints.map((c) => ({
    sx: c.lng,
    sy: c.lat,
    dx: c.px,
    dy: c.py,
  }))
  return {
    homography: computeHomography(pairs),
    calPoints,
    calByProvince: buildCalLookup(calPoints),
  }
}

function getPngBounds(provId) {
  const s = stitchData[provId]
  const d = provinceDims[provId]
  if (!s || !d) return null
  const pw = (s.scale / 100) * 500
  const ph = (pw * d.h) / d.w
  return {
    xMin: (s.x / MAP_W) * 100,
    yMin: (s.y / MAP_H) * 100,
    w: (pw / MAP_W) * 100,
    h: (ph / MAP_H) * 100,
  }
}

export function lngLatToPercent(homography, calByProvince, lng, lat, provId) {
  if (!homography) return null
  const pos = transformPoint(homography, lng, lat)
  let px = pos.px
  let py = pos.py
  if (!provId) return { px, py }
  const bounds = getPngBounds(provId)
  if (!bounds) return { px, py }
  const pad = 0.15
  const x1 = bounds.xMin + pad * bounds.w
  const x2 = bounds.xMin + (1 - pad) * bounds.w
  const y1 = bounds.yMin + pad * bounds.h
  const y2 = bounds.yMin + (1 - pad) * bounds.h
  if (px >= x1 && px <= x2 && py >= y1 && py <= y2) return { px, py }
  const anchor = calByProvince[provId]
  if (anchor) {
    const geo = provinceGeo[provId]
    const b = geo.bounds
    px = anchor.px + (lng - anchor.lng) * (bounds.w / (b[1] - b[0]))
    py = anchor.py - (lat - anchor.lat) * (bounds.h / (b[3] - b[2]))
  }
  px = Math.max(x1, Math.min(x2, px))
  py = Math.max(y1, Math.min(y2, py))
  return { px, py }
}

export function findProvinceByLngLat(lng, lat) {
  const ids = Object.keys(provinceGeo)
  let best = null
  let bestArea = Infinity
  for (let i = 0; i < ids.length; i++) {
    const id = ids[i]
    const b = provinceGeo[id].bounds
    if (lng >= b[0] && lng <= b[1] && lat >= b[2] && lat <= b[3]) {
      const area = (b[1] - b[0]) * (b[3] - b[2])
      if (area < bestArea) {
        bestArea = area
        best = { id, name: provinceGeo[id].name }
      }
    }
  }
  return best
}

export function getStitchStyle(provId) {
  const s = stitchData[provId]
  const d = provinceDims[provId]
  if (!s || !d) return null
  const pw = (s.scale / 100) * 500
  const ph = (pw * d.h) / d.w
  return {
    left: `${(s.x / MAP_W) * 100}%`,
    top: `${(s.y / MAP_H) * 100}%`,
    width: `${(pw / MAP_W) * 100}%`,
    height: `${(ph / MAP_H) * 100}%`,
  }
}

export function getDisplayRect(containerWidth, containerHeight, naturalWidth, naturalHeight) {
  if (!containerWidth || !containerHeight || !naturalWidth || !naturalHeight) {
    return { x: 0, y: 0, w: containerWidth || 1, h: containerHeight || 1 }
  }
  const scale = Math.min(containerWidth / naturalWidth, containerHeight / naturalHeight)
  const dw = naturalWidth * scale
  const dh = naturalHeight * scale
  const dx = (containerWidth - dw) / 2
  const dy = (containerHeight - dh) / 2
  return { x: dx, y: dy, w: dw, h: dh }
}

export function stateToIcon(state) {
  if (state == 2) return "gray"
  if (state == 3) return "blue"
  return "green"
}
